import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BarChart3, Info, Leaf, Loader2, Minus, TrendingDown, TrendingUp,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import MaturityTimeline, {
  MaturityChartKey, MaturityLegend,
} from '../../components/cultivar/MaturityTimeline';
import MaturityFilterBar from '../../components/cultivar/MaturityFilterBar';
import cultivarService from '../../services/cultivar';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import ModuleAccessDenied from '../../components/ui/ModuleAccessDenied';
import {
  buildCropTypeColours, colourKeyFor, hasRecordedEnd,
  type MaturityFilterState, type MaturityGroupBy,
} from '../../utils/maturityChart';
import type { MaturityChart as MaturityChartData } from '../../types/cultivar';

/**
 * The master maturity chart: every cultivar's picking window on one calendar
 * axis, coloured by crop type, filtered by crop type, region, season and date.
 *
 * It replaced a per-region modal that could only ever show one region in one
 * season. That view is this page with two filters set, which is why it is gone
 * rather than kept alongside.
 */

const GROUP_BY: MaturityGroupBy[] = ['crop_type', 'region', 'none'];

function parseIds(raw: string | null): number[] {
  if (!raw) return [];
  return raw.split(',').map(Number).filter((n) => Number.isFinite(n));
}

function MaturityChart() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Module licence, never a role check — the cultivar module is licensed.
  const { hasAccess, loading: licenceLoading } = useModuleLicense('cultivar', ['viewer', 'pbr', 'admin']);

  const [filters, setFilters] = useState<MaturityFilterState>(() => ({
    seasonYears: parseIds(searchParams.get('seasons')),
    allSeasons: searchParams.get('seasons') === 'all',
    regionIds: parseIds(searchParams.get('regions')),
    cropTypeIds: parseIds(searchParams.get('crops')),
    fromDate: searchParams.get('from') || '',
    toDate: searchParams.get('to') || '',
    includeEstimates: searchParams.get('estimates') !== 'false',
    groupBy: GROUP_BY.includes(searchParams.get('group') as MaturityGroupBy)
      ? (searchParams.get('group') as MaturityGroupBy)
      : 'crop_type',
  }));

  // Carried in the URL so the cultivar page can deep-link one cultivar into the
  // crowd rather than opening a chart where it cannot be found.
  const highlightCultivarId = searchParams.get('cultivar_id')
    ? Number(searchParams.get('cultivar_id'))
    : undefined;

  const [chart, setChart] = useState<MaturityChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cultivarService.getMaturityChart({
        seasonYears: filters.seasonYears,
        allSeasons: filters.allSeasons,
        regionIds: filters.regionIds,
        cropTypeIds: filters.cropTypeIds,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        includeEstimates: filters.includeEstimates,
      });
      if (res.success && res.data) setChart(res.data);
      setError(null);
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string };
      setError(err.response?.data?.error?.message || err.message || 'Failed to load the maturity chart');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { if (hasAccess) load(); }, [hasAccess, load]);

  // Keep the URL in step so a filtered chart can be pasted to a colleague.
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.allSeasons) params.set('seasons', 'all');
    else if (filters.seasonYears.length) params.set('seasons', filters.seasonYears.join(','));
    if (filters.regionIds.length) params.set('regions', filters.regionIds.join(','));
    if (filters.cropTypeIds.length) params.set('crops', filters.cropTypeIds.join(','));
    if (filters.fromDate) params.set('from', filters.fromDate);
    if (filters.toDate) params.set('to', filters.toDate);
    if (!filters.includeEstimates) params.set('estimates', 'false');
    if (filters.groupBy !== 'crop_type') params.set('group', filters.groupBy);
    if (highlightCultivarId) params.set('cultivar_id', String(highlightCultivarId));
    setSearchParams(params, { replace: true });
  }, [filters, highlightCultivarId, setSearchParams]);

  // Keyed off the facets, which do not move when a filter changes — so
  // switching a crop type off never repaints the ones still on screen.
  const colours = useMemo(
    () => buildCropTypeColours(chart?.facets.crop_types ?? []),
    [chart?.facets.crop_types]
  );

  const counts = useMemo(() => {
    const map = new Map<number, number>();
    for (const e of chart?.entries ?? []) {
      const key = colourKeyFor(e, colours);
      map.set(key.id, (map.get(key.id) ?? 0) + 1);
    }
    return map;
  }, [chart?.entries, colours]);

  const entries = chart?.entries ?? [];
  const recordedCount = entries.filter((e) => e.source !== 'calculated').length;
  const calculatedCount = entries.length - recordedCount;
  const assumedWindowCount = entries.filter(
    (e) => e.source !== 'calculated' && !hasRecordedEnd(e)
  ).length;
  const seasonsInView = new Set(entries.map((e) => e.season_year)).size;

  // A row only needs to name its region or season when more than one is on
  // screen; otherwise it repeats the filter on every line.
  const showRegion = new Set(entries.map((e) => e.growing_region_id)).size > 1;
  const showSeason = seasonsInView > 1;

  const index = chart?.season_index;

  // All hooks are above this line; the guards come after them.
  if (licenceLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
      </div>
    );
  }
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Maturity chart"
          subtitle="When every cultivar picks, across regions and seasons"
          icon={<BarChart3 className="h-6 w-6 text-white" />}
        />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ModuleAccessDenied moduleName="Cultivar Registry" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Maturity chart"
        subtitle="When every cultivar picks, across regions and seasons"
        icon={<BarChart3 className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="outline" onClick={() => navigate('/cultivar')}>
            <Leaf className="h-4 w-4" />
            Registry
          </Button>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="w-full lg:w-72 flex-shrink-0">
            <MaturityFilterBar
              facets={chart?.facets ?? { seasons: [], regions: [], crop_types: [] }}
              value={filters}
              onChange={setFilters}
              effectiveSeasons={chart?.filters_applied.season_years ?? []}
            />
          </div>

          <div className="flex-1 min-w-0 space-y-4">
            {error && (
              <div className="bg-error-50 border border-error-200 rounded-xl p-4 text-center">
                <p className="text-error-700 mb-3 text-sm">{error}</p>
                <Button variant="outline" onClick={load}>Try again</Button>
              </div>
            )}

            {/* Season earliness. Defined only for one region in one season, so
                it simply is not there for any wider view rather than being
                shown as a number about everything. */}
            {index && (
              <div className={`rounded-lg border p-3 flex items-start gap-2 ${
                index.direction === 'early' ? 'bg-primary-50 border-primary-200'
                  : index.direction === 'late' ? 'bg-warning-50 border-warning-200'
                    : 'bg-neutral-50 border-neutral-200'
              }`}>
                {index.direction === 'early' ? <TrendingDown className="h-4 w-4 mt-0.5 text-primary-700 shrink-0" />
                  : index.direction === 'late' ? <TrendingUp className="h-4 w-4 mt-0.5 text-warning-700 shrink-0" />
                    : <Minus className="h-4 w-4 mt-0.5 text-neutral-500 shrink-0" />}
                <div className="text-sm min-w-0">
                  <span className="font-medium">
                    {index.direction === 'typical'
                      ? 'Season running to type'
                      : `Season running ${Math.abs(index.deviation_days)} days ${index.direction}`}
                  </span>
                  <span className="block text-xs text-neutral-600 mt-0.5">
                    Against this region's own history, across {index.cultivar_count} cultivar
                    {index.cultivar_count === 1 ? '' : 's'} · ±{index.stddev_days}d spread ·
                    {' '}confidence {Math.round(index.confidence * 100)}%
                  </span>
                </div>
              </div>
            )}

            {chart && chart.facets.crop_types.length > 0 && (
              <MaturityLegend
                colours={colours}
                counts={counts}
                selected={new Set(filters.cropTypeIds)}
                onToggle={(id) => setFilters((f) => ({
                  ...f,
                  cropTypeIds: f.cropTypeIds.includes(id)
                    ? f.cropTypeIds.filter((x) => x !== id)
                    : [...f.cropTypeIds, id],
                }))}
                onShowAll={() => setFilters((f) => ({ ...f, cropTypeIds: [] }))}
              />
            )}

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-neutral-500 py-20 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading chart…
              </div>
            ) : entries.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-10 text-center">
                <div className="h-14 w-14 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="h-7 w-7 text-neutral-400" />
                </div>
                <h3 className="text-base font-semibold text-neutral-900 mb-2">Nothing to plot</h3>
                <p className="text-sm text-neutral-600 max-w-md mx-auto">
                  {chart && chart.facets.seasons.length === 0
                    ? 'No maturity records exist yet. Add a first pick date on a cultivar’s Maturity tab and it will appear here.'
                    : 'No maturity records match these filters. Widen the season, region or date window.'}
                </p>
              </div>
            ) : (
              <>
                <MaturityTimeline
                  entries={entries}
                  colours={colours}
                  groupBy={filters.groupBy}
                  highlightCultivarId={highlightCultivarId}
                  showRegion={showRegion}
                  showSeason={showSeason}
                  onSelectCultivar={(id) => navigate(`/cultivar/${id}`)}
                />

                {chart?.truncated && (
                  <p className="text-xs text-warning-800 bg-warning-50 border border-warning-200 rounded-lg p-2.5 flex items-start gap-1.5">
                    <Info className="h-3.5 w-3.5 mt-px shrink-0" />
                    Showing the first {entries.length} of {chart.total_matched} entries. Narrow the
                    season, region or crop type to see the rest.
                  </p>
                )}

                <MaturityChartKey
                  recordedCount={recordedCount}
                  calculatedCount={calculatedCount}
                  assumedWindowCount={assumedWindowCount}
                  foldedSeasons={seasonsInView}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaturityChart;
